import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, TrendingUp, Volume2 } from "lucide-react";
import { api, formatApiErrorDetail, money, shopTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LiveSpotPanel } from "@/components/LiveSpotPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PURITIES = ["24K", "22K", "21K", "18K", "14K"];
// Fineness of each purity. 24K is the base the owner types; the rest follow automatically.
const PURITY_FACTOR = { "24K": 1, "22K": 0.916, "21K": 0.875, "18K": 0.75, "14K": 0.585 };
const autoFrom24 = (base) =>
  PURITIES.reduce((acc, p) => ({ ...acc, [p]: (base * PURITY_FACTOR[p]).toFixed(2) }), {});

export default function GoldRates() {
  const { branch } = useAuth();
  const [rates, setRates] = useState({});
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");
  const [spotUsed, setSpotUsed] = useState({});
  const [abcBusy, setAbcBusy] = useState(false);

  const useSpot = (purity, value) => {
    if (purity === "24K") {
      const base = Number(value);
      setDraft((d) => ({ ...d, ...autoFrom24(base), "24K": String(value) }));
      setSpotUsed({ "24K": value });
      toast.success(`24K filled with ${money(value)}; other carats auto-calculated — press Set to make live`);
    } else {
      setDraft((d) => ({ ...d, [purity]: value }));
      setSpotUsed((s) => ({ ...s, [purity]: value }));
      toast.success(`${purity} box filled with ${money(value)} — press Set to make it live`);
    }
  };

  // Typing 24K recalculates the rest; editing any other carat only changes that one.
  const onRateChange = (purity, value) => {
    if (purity === "24K") {
      const base = Number(value);
      setDraft((d) => ({ ...d, ...(base > 0 ? autoFrom24(base) : {}), "24K": value }));
      setSpotUsed({});
    } else {
      setDraft((d) => ({ ...d, [purity]: value }));
    }
  };

  const audioUrl =
    `${process.env.REACT_APP_BACKEND_URL}/api/public/voice/rates/${branch}/announcement`;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — select the link below instead");
    }
  };

  const load = () => {
    api
      .get("/gold-rates", { params: { branch } })
      .then(({ data }) => {
        setRates(data);
        setDraft(
          PURITIES.reduce((acc, p) => ({ ...acc, [p]: data[p]?.rate ?? "" }), {})
        );
      })
      .catch(() => setRates({}));
    api
      .get("/gold-rates/history", { params: { branch } })
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory([]));
  };

  useEffect(load, [branch]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (purity) => {
    const rate = Number(draft[purity]);
    if (!rate || rate <= 0) return toast.error("Enter a rate greater than zero");
    setBusy(purity);
    try {
      await api.post("/gold-rates", {
        branch,
        purity,
        rate,
        ...(spotUsed[purity] ? { spot_source: "live", spot_per_gram: spotUsed[purity] } : {}),
      });
      toast.success(`${purity} rate set to ${money(rate)}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const saveAll = async () => {
    const entries = PURITIES.map((p) => [p, Number(draft[p])]).filter(([, r]) => r > 0);
    if (!entries.length) return toast.error("Enter the 24K rate first");
    setBusy("all");
    try {
      for (const [p, rate] of entries) {
        await api.post("/gold-rates", {
          branch,
          purity: p,
          rate,
          ...(spotUsed[p] ? { spot_source: "live", spot_per_gram: spotUsed[p] } : {}),
        });
      }
      toast.success(`All ${entries.length} gold rates updated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const fetchAbc = async () => {
    setAbcBusy(true);
    try {
      const { data } = await api.get("/gold-rates/abc-bullion");
      if (!data.available) {
        toast.error(data.reason || "ABC Bullion unavailable — please type the 24K rate in manually");
        return;
      }
      onRateChange("24K", String(data.per_gram));
      toast.success(
        `ABC Bullion 24K ≈ ${money(data.per_gram)}/g (from their ${money(data.per_unit)} 100g bar) — review and Set`
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAbcBusy(false);
    }
  };

  return (
    <div className="fade-up space-y-7" data-testid="gold-rates-page">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
          {branch}
        </p>
        <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Today's Gold Rate</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter the <strong>24K</strong> rate and the other carats fill in automatically
          (22K &times;0.916, 21K &times;0.875, 18K &times;0.750, 14K &times;0.585) — you can still
          edit any of them. Press <em>Set all rates</em> to publish them in one go, or use Set on
          each card. The in-store TV board shows 24K, 22K, 18K and 14K.
        </p>
      </div>

      <LiveSpotPanel branch={branch} onUse={useSpot} />

      <div className="border border-border bg-[#FBF9F5] p-5" data-testid="phone-line-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Phone rate announcement</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              These addresses read out the rates above. Give them to whoever manages the
              phone system, or download the recording to upload to the IVR. It updates
              itself every time you change a rate.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="h-10 rounded-none"
              onClick={() => window.open(`${audioUrl}.mp3`, "_blank")}
              data-testid="listen-announcement-button"
            >
              <Volume2 className="mr-2 h-4 w-4" /> Listen
            </Button>
            <Button
              variant="outline"
              className="h-10 rounded-none"
              onClick={() => copy(`${audioUrl}.wav`)}
              data-testid="copy-wav-url-button"
            >
              <Copy className="mr-2 h-4 w-4" /> Copy WAV link
            </Button>
          </div>
        </div>
        <div className="mt-4 space-y-1.5 break-all font-mono text-xs text-muted-foreground">
          <p data-testid="announcement-url-wav">
            {audioUrl}.wav — 8 kHz mono, for most phone systems
          </p>
          <p data-testid="announcement-url-mp3">
            {audioUrl}.mp3 — MP3, for anything that accepts it
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Set the 24K rate (or pull it from ABC Bullion), glance at the rest, then publish.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchAbc}
            disabled={abcBusy}
            className="h-10 rounded-none"
            data-testid="fetch-abc-button"
          >
            {abcBusy ? "Fetching…" : "Fetch 24K from ABC Bullion"}
          </Button>
          <Button
            onClick={saveAll}
            disabled={busy === "all"}
            className="h-10 rounded-none bg-[#0A0A0A] px-5 font-semibold text-white hover:bg-black/80"
            data-testid="rate-save-all"
          >
            {busy === "all" ? "Setting…" : "Set all rates"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {PURITIES.map((p) => (
          <div key={p} className="bg-white p-5" data-testid={`rate-card-${p}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                {p}
              </span>
              <TrendingUp className="h-4 w-4 text-[#C5A059]" />
            </div>
            <div className="tabular mt-3 text-2xl font-bold" data-testid={`rate-value-${p}`}>
              {rates[p] ? money(rates[p].rate) : "Not set"}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {rates[p]
                ? `Set ${rates[p].rate_date} by ${rates[p].set_by}`
                : "No rate recorded yet"}
            </div>
            <div className="mt-4 space-y-2">
              <Label className="text-[11px] uppercase tracking-wider">
                New rate (per gram)
              </Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  className="h-10 rounded-none"
                  value={draft[p] ?? ""}
                  onChange={(e) => onRateChange(p, e.target.value)}
                  data-testid={`rate-input-${p}`}
                />
                <Button
                  onClick={() => save(p)}
                  disabled={busy === p}
                  className="h-10 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                  data-testid={`rate-save-${p}`}
                >
                  Set
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                {p === "24K" ? "Base rate — type this one" : `Auto: 24K × ${PURITY_FACTOR[p]} · editable`}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-border">
        <div className="border-b border-border bg-[#F8F9FA] px-4 py-3">
          <h2 className="text-sm font-bold uppercase tracking-wider">Rate history</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm" data-testid="rate-history-table">
            <thead className="bg-white">
              <tr className="border-b border-border">
                <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-left font-semibold">Time</th>
                <th className="px-3 py-2.5 text-left font-semibold">Purity</th>
                <th className="px-3 py-2.5 text-right font-semibold">Rate</th>
                <th className="px-3 py-2.5 text-right font-semibold">Spot at the time</th>
                <th className="px-3 py-2.5 text-left font-semibold">Set by</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {history.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    No rates recorded for {branch} yet.
                  </td>
                </tr>
              )}
              {history.map((h) => (
                <tr key={h.id} className="hover:bg-[#FBF8F2]">
                  <td className="tabular px-3 py-2">{h.rate_date}</td>
                  <td className="tabular px-3 py-2 text-muted-foreground">
                    {shopTime(h.created_at)}
                  </td>
                  <td className="px-3 py-2">{h.purity}</td>
                  <td className="tabular px-3 py-2 text-right font-semibold">
                    {money(h.rate)}
                  </td>
                  <td className="tabular px-3 py-2 text-right text-muted-foreground">
                    {h.spot_per_gram ? money(h.spot_per_gram) : "-"}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{h.set_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
